/**
 * useMembership Hook
 * Notes+ - Bosph Technologies
 * 
 * Kullanıcının üyelik tipine göre özellik erişimlerini kontrol eden hook.
 * Offline-first mimarimize uygun olarak, kontroller local veritabanı verilerine dayanır.
 */

import { useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import i18n from '../i18n';
import {
  MEMBERSHIP_TYPES,
  getLimitsForMembership,
  getFeatureLimit,
  hasPlusAccess as checkPlusAccess,
  canUseTheme as checkCanUseTheme,
  FEATURE_NAMES,
  UPGRADE_MESSAGES,
} from '../utils/membershipConstants';

/**
 * Üyelik ve özellik erişim kontrolü için custom hook
 * @returns {object} Üyelik durumu ve kontrol fonksiyonları
 */
export const useMembership = () => {
  const { user } = useAuth();

  // Kullanıcının üyelik tipini belirle
  const membershipType = useMemo(() => {
    if (!user) return MEMBERSHIP_TYPES.FREE;
    
    // Admin kontrolü - her zaman Plus gibi davranır
    if (
      user.role === 'admin' ||
      user.isAdmin === true ||
      user.isAdmin === 1 ||
      user.membershipType === 'admin' ||
      user.membership_type === 'admin'
    ) {
      return MEMBERSHIP_TYPES.ADMIN;
    }
    
    // Plus kontrolü — /me effective role; defensive expiry for stale local cache
    if (
      user.role === 'plus' ||
      user.membershipType === 'plus' ||
      user.membership_type === 'plus'
    ) {
      const expiryRaw = user.plusExpiryDate || user.plus_expiry_date;
      if (expiryRaw) {
        const exp = new Date(expiryRaw);
        if (!Number.isNaN(exp.getTime()) && exp.getTime() <= Date.now()) {
          return MEMBERSHIP_TYPES.FREE;
        }
      }
      return MEMBERSHIP_TYPES.PLUS;
    }
    
    // Varsayılan: Free
    return MEMBERSHIP_TYPES.FREE;
  }, [user]);

  // Mevcut limitleri al
  const limits = useMemo(() => getLimitsForMembership(membershipType), [membershipType]);

  // Plus erişimi var mı?
  const hasPlusAccess = useMemo(() => checkPlusAccess(membershipType), [membershipType]);

  // Free kullanıcı mı?
  const isFreeUser = useMemo(() => membershipType === MEMBERSHIP_TYPES.FREE, [membershipType]);

  // Admin mi?
  const isAdmin = useMemo(() => membershipType === MEMBERSHIP_TYPES.ADMIN, [membershipType]);

  /**
   * Belirli bir özelliğin limit değerini döndürür
   * @param {string} featureName - Özellik adı
   * @returns {any} Limit değeri
   */
  const getLimit = useCallback(
    (featureName) => getFeatureLimit(membershipType, featureName),
    [membershipType]
  );

  /**
   * Yeni not oluşturulabilir mi kontrol eder
   * @param {number} currentCount - Mevcut not sayısı
   * @returns {boolean}
   */
  const canCreateNote = useCallback(
    (currentCount) => {
      const maxNotes = limits.maxNoteCount;
      if (maxNotes === Infinity) return true;
      return currentCount < maxNotes;
    },
    [limits.maxNoteCount]
  );

  /**
   * Nota resim/medya eklenebilir mi kontrol eder
   * @returns {boolean}
   */
  const canAddImage = useCallback(() => {
    return limits.mediaInNotes;
  }, [limits.mediaInNotes]);

  /**
   * Belirli bir tema kullanılabilir mi kontrol eder
   * @param {string} themeName - Tema adı ('midnight')
   * @returns {boolean}
   */
  const canUseTheme = useCallback(
    (themeName) => checkCanUseTheme(membershipType, themeName),
    [membershipType]
  );

  /**
   * Yeni takvim oluşturulabilir mi kontrol eder
   * @param {number} currentCount - Mevcut takvim sayısı
   * @returns {boolean}
   */
  const canCreateCalendar = useCallback(
    (currentCount) => {
      const maxCalendars = limits.maxCalendars;
      if (maxCalendars === Infinity) return true;
      return currentCount < maxCalendars;
    },
    [limits.maxCalendars]
  );

  /**
   * Tekrarlayan etkinlik oluşturulabilir mi kontrol eder
   * @returns {boolean}
   */
  const canUseRecurringEvents = useCallback(() => {
    return limits.recurringEvents;
  }, [limits.recurringEvents]);

  /**
   * Özel mola sesleri kullanılabilir mi kontrol eder
   * @returns {boolean}
   */
  const canUseCustomBreakSounds = useCallback(() => {
    return limits.customBreakSounds;
  }, [limits.customBreakSounds]);

  /**
   * Otomatik senkronizasyon aktif mi kontrol eder
   * @returns {boolean}
   */
  const hasAutoSync = useCallback(() => {
    return limits.autoSync;
  }, [limits.autoSync]);

  /**
   * Kalan not sayısını hesaplar
   * @param {number} currentCount - Mevcut not sayısı
   * @returns {number|null} Kalan not sayısı veya null (sınırsız ise)
   */
  const getRemainingNotes = useCallback(
    (currentCount) => {
      const maxNotes = limits.maxNoteCount;
      if (maxNotes === Infinity) return null;
      return Math.max(0, maxNotes - currentCount);
    },
    [limits.maxNoteCount]
  );

  /**
   * Kalan takvim sayısını hesaplar
   * @param {number} currentCount - Mevcut takvim sayısı
   * @returns {number|null} Kalan takvim sayısı veya null (sınırsız ise)
   */
  const getRemainingCalendars = useCallback(
    (currentCount) => {
      const maxCalendars = limits.maxCalendars;
      if (maxCalendars === Infinity) return null;
      return Math.max(0, maxCalendars - currentCount);
    },
    [limits.maxCalendars]
  );

  /**
   * Upgrade mesajını döndürür
   * @param {string} featureKey - Özellik anahtarı (UPGRADE_MESSAGES'tan)
   * @returns {string}
   */
  const getUpgradeMessage = useCallback((featureKey) => {
    return UPGRADE_MESSAGES[featureKey] || i18n.t('shared:upgradeMessages.genericPlusOnly');
  }, []);

  return {
    // Üyelik bilgileri
    membershipType,
    limits,
    hasPlusAccess,
    isFreeUser,
    isAdmin,
    
    // Limit sorgulama
    getLimit,
    
    // Not kontrolleri
    canCreateNote,
    canAddImage,
    getRemainingNotes,
    
    // Tema kontrolü
    canUseTheme,
    
    // Takvim kontrolleri
    canCreateCalendar,
    canUseRecurringEvents,
    getRemainingCalendars,
    
    // Diğer özellikler
    canUseCustomBreakSounds,
    hasAutoSync,
    
    // Yardımcı
    getUpgradeMessage,
    
    // Sabitler (re-export)
    FEATURE_NAMES,
    UPGRADE_MESSAGES,
  };
};

export default useMembership;














